"use client"

import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { ArrowLeft, Share2, Lock } from "lucide-react"
import type { PreviewData } from "@/lib/types"
import { useTranslation } from "react-i18next"

interface PreviewHeaderProps {
  preview: PreviewData
  onNavigateBack: () => void
  formatDate: (date: string) => string
}

export function PreviewHeader({ preview, onNavigateBack, formatDate }: PreviewHeaderProps) {
    const {t} = useTranslation("common")

    return (
    <div className="space-y-4 pb-6 border-b">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={onNavigateBack} className="gap-2">
          <ArrowLeft className="h-4 w-4" />
          <span className="hidden sm:inline">{t("common:previewViewer.header.backTo")}</span>
          <span className="sm:hidden">{t("common:previewViewer.header.back")}</span>
        </Button>
      </div>

      <div className="space-y-3">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="space-y-1">
            <h1 className="text-2xl font-bold tracking-tight">{preview.name}</h1>
            <p className="text-sm text-muted-foreground">{t("common:previewViewer.header.created" , {time:formatDate(preview.created_at)})}</p>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant={preview.is_sharable ? "default" : "secondary"} className="gap-1">
              {preview.is_sharable ? (
                <>
                  <Share2 className="h-3 w-3" />
                  {t("common:previewViewer.header.shareable")}
                </>
              ) : (
                <>
                  <Lock className="h-3 w-3" />
                  {t("common:previewViewer.header.private")}
                </>
              )}
            </Badge>
            {preview.model?.name && <Badge variant="outline">{preview.model.name}</Badge>}
          </div>
        </div>

        {preview.tags && preview.tags.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {preview.tags.map((tag, index) => (
              <Badge key={index} variant="secondary" className="text-xs">
                {tag}
              </Badge>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
